/**
 * ListingPreviewPane.tsx
 *
 * Multi-Channel In-Memory Listing Simulator with RTL Bidirectional Switching:
 * - Viewports: Desktop Storefront PDP, Mobile Web Card, POS 48mm Quick-Tap Tile, Kiosk Display, SEO Card.
 * - Zero database writes; pipes draft directly into audience-specific layout.
 * - Multi-locale switcher (EN, ES, ZH, AR, FR, DE) with live RTL container mirroring.
 */

import React, { useState } from 'react';
import { globalMediaAssetService } from '../../plugins/commerce/media/mediaAssetService.js';
import { SeoDiagnosticsCard } from './SeoDiagnosticsCard.js';
import type { Product, ProductVariant } from '../../plugins/commerce/commerce-contracts.js';
import type { MediaAsset } from '../../plugins/commerce/inventory/inventoryContracts.js';
import type { AuthorizationContext } from '../../kernel/authorization/capabilities.js';

export type PreviewChannel = 'storefront-desktop' | 'storefront-mobile' | 'pos-tile' | 'kiosk-display' | 'seo-snippet';

export interface ListingPreviewPaneProps {
  readonly draftProduct: Partial<Product>;
  readonly draftVariants: readonly Partial<ProductVariant>[];
  readonly draftMedia: readonly MediaAsset[];
  readonly authContext?: AuthorizationContext;
}

const LOCALES = [
  { code: 'en-US', flag: '🇺🇸', label: 'EN' },
  { code: 'es-ES', flag: '🇪🇸', label: 'ES' },
  { code: 'zh-Hans', flag: '🇨🇳', label: 'ZH' },
  { code: 'ar-SA', flag: '🇸🇦', label: 'AR' },
  { code: 'fr-FR', flag: '🇫🇷', label: 'FR' },
  { code: 'de-DE', flag: '🇩🇪', label: 'DE' },
];

export const ListingPreviewPane: React.FC<ListingPreviewPaneProps> = ({
  draftProduct,
  draftVariants,
  draftMedia,
  authContext,
}) => {
  const [channel, setChannel] = useState<PreviewChannel>('storefront-desktop');
  const [activeLocale, setActiveLocale] = useState('en-US');
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);

  const isRtl = activeLocale.startsWith('ar');

  const titleText =
    typeof draftProduct.title === 'string'
      ? draftProduct.title
      : draftProduct.title?.[activeLocale] ||
        draftProduct.title?.['en-US'] ||
        Object.values(draftProduct.title || {})[0] ||
        'Untitled Product';

  const descText =
    typeof draftProduct.description === 'string'
      ? draftProduct.description
      : draftProduct.description?.[activeLocale] ||
        draftProduct.description?.['en-US'] ||
        Object.values(draftProduct.description || {})[0] ||
        'No description provided.';

  const selectedVariant = draftVariants[selectedVariantIndex] || draftVariants[0] || {};
  const priceMinor = parseInt(selectedVariant.basePriceMinor || '0', 10);
  const formattedPrice = (priceMinor / 100).toFixed(2);
  const currency = selectedVariant.currency || 'USD';

  const primaryMedia = draftMedia.find((m) => m.role === 'primary') || draftMedia[0];
  const primaryImageUrl = primaryMedia
    ? globalMediaAssetService.resolveDisplayUrl(primaryMedia.mediaId, authContext)
    : 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="300" height="300" viewBox="0 0 300 300"><rect width="300" height="300" fill="%231e293b"/><text x="150" y="155" fill="%2364748b" font-family="sans-serif" font-size="14" font-weight="700" text-anchor="middle">No Media Attached</text></svg>';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: '#0a0e17',
        borderLeft: '1px solid var(--theme-border, #1e293b)',
        borderRadius: '0 12px 12px 0',
        overflow: 'hidden',
      }}
    >
      {/* Top Toolbar: Viewports & Multi-Locale Switcher */}
      <div
        style={{
          padding: '10px 14px',
          backgroundColor: '#111827',
          borderBottom: '1px solid var(--theme-border, #1e293b)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '8px',
        }}
      >
        {/* Channel Selector Pills */}
        <div style={{ display: 'flex', gap: '4px' }}>
          {[
            { id: 'storefront-desktop', label: '🖥️ Desktop' },
            { id: 'storefront-mobile', label: '📱 Mobile' },
            { id: 'pos-tile', label: '📟 POS' },
            { id: 'kiosk-display', label: '🏛️ Kiosk' },
            { id: 'seo-snippet', label: '🔍 SEO' },
          ].map((ch) => (
            <button
              key={ch.id}
              type="button"
              onClick={() => setChannel(ch.id as PreviewChannel)}
              style={{
                padding: '4px 8px',
                borderRadius: '6px',
                fontSize: '11px',
                fontWeight: 700,
                cursor: 'pointer',
                border: 'none',
                backgroundColor: channel === ch.id ? 'var(--theme-accent, #38bdf8)' : 'rgba(255, 255, 255, 0.06)',
                color: channel === ch.id ? '#000' : 'var(--theme-text-secondary, #94a3b8)',
              }}
            >
              {ch.label}
            </button>
          ))}
        </div>

        {/* Locale Flags */}
        <div style={{ display: 'flex', gap: '4px' }}>
          {LOCALES.map((loc) => (
            <button
              key={loc.code}
              type="button"
              onClick={() => setActiveLocale(loc.code)}
              title={loc.label}
              style={{
                padding: '3px 6px',
                borderRadius: '4px',
                fontSize: '10px',
                fontWeight: 700,
                cursor: 'pointer',
                backgroundColor: activeLocale === loc.code ? '#38bdf8' : 'rgba(255, 255, 255, 0.05)',
                color: activeLocale === loc.code ? '#000' : '#94a3b8',
                border: '1px solid #334155',
              }}
            >
              {loc.flag} {loc.label}
            </button>
          ))}
        </div>
      </div>

      {/* Live Viewport Container */}
      <div
        dir={isRtl ? 'rtl' : 'ltr'}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
        }}
      >
        {/* 1. DESKTOP STOREFRONT VIEWPORT */}
        {channel === 'storefront-desktop' && (
          <div
            style={{
              width: '100%',
              maxWidth: '480px',
              backgroundColor: '#151d2a',
              borderRadius: '12px',
              border: '1px solid #1e293b',
              overflow: 'hidden',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
            }}
          >
            <div style={{ position: 'relative', width: '100%', aspectRatio: '16/10', backgroundColor: '#000' }}>
              <img
                src={primaryImageUrl}
                alt={titleText}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
              <span
                style={{
                  position: 'absolute',
                  top: '10px',
                  insetInlineStart: '10px',
                  backgroundColor: draftProduct.status === 'active' ? '#065f46' : '#78350f',
                  color: draftProduct.status === 'active' ? '#34d399' : '#fcd34d',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: '10px',
                  fontWeight: 800,
                  textTransform: 'uppercase',
                }}
              >
                {draftProduct.status || 'draft'}
              </span>
            </div>

            <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ fontSize: '11px', color: '#38bdf8', fontWeight: 700, textTransform: 'uppercase' }}>
                {draftProduct.categoryIds?.[0] || 'Uncategorized'}
              </div>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 800, color: '#f8fafc', lineHeight: '1.3' }}>
                {titleText}
              </h3>
              <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8', lineHeight: '1.4' }}>
                {descText}
              </p>

              {/* Price & Variant Bar */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: '6px' }}>
                <span style={{ fontSize: '20px', fontWeight: 800, color: '#34d399', unicodeBidi: 'isolate' }}>
                  ${formattedPrice} <span style={{ fontSize: '11px', color: '#94a3b8' }}>{currency}</span>
                </span>
                <span style={{ fontSize: '11px', color: '#34d399', fontWeight: 600 }}>
                  ● In Stock
                </span>
              </div>

              {/* Variant Selector Pills */}
              {draftVariants.length > 1 && (
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '4px' }}>
                  {draftVariants.map((v, i) => (
                    <button
                      key={v.variantId || i}
                      type="button"
                      onClick={() => setSelectedVariantIndex(i)}
                      style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        backgroundColor: selectedVariantIndex === i ? '#38bdf8' : 'rgba(255,255,255,0.05)',
                        color: selectedVariantIndex === i ? '#000' : '#cbd5e1',
                        border: '1px solid #334155',
                      }}
                    >
                      {v.sku}
                    </button>
                  ))}
                </div>
              )}

              <button
                type="button"
                style={{
                  marginTop: '8px',
                  padding: '10px',
                  borderRadius: '8px',
                  backgroundColor: '#38bdf8',
                  color: '#000',
                  fontWeight: 800,
                  fontSize: '12px',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                🛒 Add to Cart
              </button>
            </div>
          </div>
        )}

        {/* 2. MOBILE CARD VIEWPORT */}
        {channel === 'storefront-mobile' && (
          <div
            style={{
              width: '280px',
              backgroundColor: '#151d2a',
              borderRadius: '16px',
              border: '2px solid #334155',
              overflow: 'hidden',
              padding: '12px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
            }}
          >
            <div style={{ width: '100%', aspectRatio: '1', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#000' }}>
              <img src={primaryImageUrl} alt={titleText} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            <div style={{ fontSize: '13px', fontWeight: 800, color: '#f8fafc', lineHeight: '1.2' }}>{titleText}</div>
            <div style={{ fontSize: '11px', color: '#94a3b8', lineClamp: 2 }}>{descText.slice(0, 75)}...</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
              <span style={{ fontSize: '15px', fontWeight: 800, color: '#34d399' }}>${formattedPrice}</span>
              <button
                type="button"
                style={{ padding: '4px 10px', borderRadius: '6px', backgroundColor: '#38bdf8', color: '#000', fontWeight: 700, fontSize: '11px', border: 'none' }}
              >
                + Add
              </button>
            </div>
          </div>
        )}

        {/* 3. POS 48mm GRID TILE VIEWPORT */}
        {channel === 'pos-tile' && (
          <div
            style={{
              width: '180px',
              height: '180px',
              backgroundColor: '#1e293b',
              borderRadius: '12px',
              border: '2px solid #38bdf8',
              padding: '12px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              boxShadow: '0 4px 14px rgba(0,0,0,0.4)',
              cursor: 'pointer',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <code style={{ fontSize: '10px', color: '#94a3b8', unicodeBidi: 'isolate' }}>{selectedVariant.sku || 'SKU'}</code>
              <span style={{ fontSize: '9px', fontWeight: 800, backgroundColor: '#065f46', color: '#34d399', padding: '1px 4px', borderRadius: '3px' }}>POS</span>
            </div>
            <div style={{ fontSize: '13px', fontWeight: 800, color: '#f8fafc', lineHeight: '1.2' }}>
              {titleText.slice(0, 32)}
            </div>
            <div style={{ fontSize: '18px', fontWeight: 900, color: '#34d399', textAlign: isRtl ? 'left' : 'right' }}>
              ${formattedPrice}
            </div>
          </div>
        )}

        {/* 4. SELF-SERVICE KIOSK VIEWPORT */}
        {channel === 'kiosk-display' && (
          <div
            style={{
              width: '320px',
              backgroundColor: '#0f172a',
              borderRadius: '16px',
              border: '3px solid #38bdf8',
              overflow: 'hidden',
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              boxShadow: '0 12px 36px rgba(56, 189, 248, 0.2)',
            }}
          >
            <div style={{ width: '100%', height: '140px', borderRadius: '10px', overflow: 'hidden', backgroundColor: '#000' }}>
              <img src={primaryImageUrl} alt={titleText} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            <div style={{ fontSize: '16px', fontWeight: 900, color: '#f8fafc' }}>{titleText}</div>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', backgroundColor: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', fontWeight: 700 }}>
                🛡️ Verified Allergen Safe
              </span>
              <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', backgroundColor: 'rgba(52, 211, 153, 0.15)', color: '#34d399', fontWeight: 700 }}>
                ✨ Fresh Batch
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px' }}>
              <span style={{ fontSize: '22px', fontWeight: 900, color: '#34d399' }}>${formattedPrice}</span>
              <button
                type="button"
                style={{ padding: '8px 16px', borderRadius: '8px', backgroundColor: '#38bdf8', color: '#000', fontWeight: 900, fontSize: '12px', border: 'none' }}
              >
                Touch to Order
              </button>
            </div>
          </div>
        )}

        {/* 5. SEO & OPENGRAPH CARD VIEWPORT */}
        {channel === 'seo-snippet' && (
          <div style={{ width: '100%', maxWidth: '480px' }}>
            <SeoDiagnosticsCard
              product={draftProduct}
              variants={draftVariants}
              media={draftMedia}
              locale={activeLocale}
            />
          </div>
        )}
      </div>
    </div>
  );
};
